<template>
  <div v-if="showDialog" class="expense-sheets-module">
    
    <div class="expense-sheets-container pt-0">
      <div class="expense-sheets-content" @wheel.stop>
        
        <!-- Tabs de navegación -->
        <Tabs :value="activeTab" @update:value="activeTab = Number($event)" class="custom-tabs">
          <TabList>
            <Tab :value="0">{{ t('wkt.client.expensesheets.table.tabs.pending').toUpperCase() }}</Tab>
            <Tab :value="1">{{ t('wkt.client.expensesheets.table.tabs.completed').toUpperCase() }}</Tab>
          </TabList>

          <TabPanels>
            <!-- Panel Pendientes -->
            <TabPanel :value="0">
              <div v-if="isLoading" class="loading-container">
                <ProgressSpinner />
              </div>
              
              <div v-else>
                <EmptyState
                  v-if="pendingItems.length === 0"
                  :title="t('wkt.client.expensesheets.no_data')"
                  :description="t('wkt.client.expensesheets.no_data')"
                />
                
                <div v-else>
                  <!-- DESKTOP: Table view -->
                  <ExpenseSheetsTable 
                    v-if="!isMobile"
                    :items="pendingItems"
                    @refresh="loadExpenseSheets"
                    @show-detail="showDetail"
                  />
                  
                  <!-- MOBILE: Card list view -->
                  <ExpenseSheetCardList 
                    v-else
                    :items="pendingItems"
                    @refresh="loadExpenseSheets"
                    @show-detail="showDetail"
                  />
                </div>
              </div>
            </TabPanel>

            <!-- Panel Completadas -->
            <TabPanel :value="1">
              <div v-if="isLoading" class="loading-container">
                <ProgressSpinner />
              </div>
              
              <div v-else>
                <EmptyState
                  v-if="completedItems.length === 0"
                  :title="t('wkt.client.expensesheets.no_data')"
                  :description="t('wkt.client.expensesheets.no_data')"
                />
                
                <div v-else>
                  <!-- DESKTOP: Table view -->
                  <ExpenseSheetsTable 
                    v-if="!isMobile"
                    :items="completedItems"
                    @refresh="loadExpenseSheets"
                    @show-detail="showDetail"
                  />
                  
                  <!-- MOBILE: Card list view -->
                  <ExpenseSheetCardList 
                    v-else
                    :items="completedItems"
                    @show-detail="showDetail"
                  />
                </div>
              </div>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </div>

     <!-- Botón flotante agregar (solo en tab pendientes) -->
        <Button
          v-if="!isLoading && activeTab === 0 && showAddButton"
          icon="pi pi-plus"
		      size="large"
          class="custom-add-button"
          :class="{ 'custom-add-button-mobile': isMobile }"
          rounded
          severity="var(--primary-color)"
          @click="addItem"
          v-tooltip="{ value: t('wkt.client.expensesheets.add_expense_sheet') }"
        />

        <!-- Toast para mensajes -->
        <Toast position="bottom-center" />

    <!-- Diálogo de creación/edición -->
    <ExpenseSheetDialog
      ref="expenseSheetDialog"
      @item-updated="onItemUpdated"
      @item-deleted="onItemDeleted"
    />
  </div>

  <!-- Diálogo de detalle (fuera del v-if, usa Teleport interno) -->
  <ExpenseSheetDetailDialog
    ref="expenseSheetDetailDialog"
    @refresh="loadExpenseSheets"
    @closed="onDetailDialogClosed"
    @send-success="onSendSuccess"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast'
import { useAlert } from '@wkt/composables/useAlert'
import EmptyState from '@wkt/components/common/EmptyState.vue'
import ExpenseSheetsTable from './ExpenseSheetsTable.vue'
import ExpenseSheetCardList from './ExpenseSheetCardList.vue'
import ExpenseSheetDialog from './ExpenseSheetDialog.vue'
import ExpenseSheetDetailDialog from './ExpenseSheetDetailDialog.vue'
import expenseSheetsApi from '@wkt/api/expense-sheets'
import { gwtApi } from '@wkt/api/gwt/gwtAPI'
import type { ExpenseSheet } from '@wkt/api/expense-sheets'

// Composables
const { t } = useI18n()
const toast = useToast()
const { showAlert } = useAlert()

// State
const showDialog = ref(true)
const isLoading = ref(false)
const activeTab = ref(0)
const items = ref<ExpenseSheet[]>([])
const company = ref<any>(null)
const showAddButton = ref(true)
const isMobile = ref(false)
const expenseSheetDialog = ref<InstanceType<typeof ExpenseSheetDialog> | null>(null)
const expenseSheetDetailDialog = ref<InstanceType<typeof ExpenseSheetDetailDialog> | null>(null)

// Computed
const pendingItems = computed(() => {
  // Estados pendientes: -1 (preparando), 0 (pendiente), 1 (aprobado), 2 (rechazado)
  return items.value.filter(i => {
    const status = Number(i.status)
    return [-1, 0, 1, 2].includes(status)
  })
})

const completedItems = computed(() => {
  // Estados completados: 3 (cancelado), 4 (procesado)
  return items.value.filter(i => {
    const status = Number(i.status)
    return [3, 4].includes(status)
  })
})

// Methods
function addItem() {
  if (expenseSheetDialog.value) {
    expenseSheetDialog.value.open(null, (updatedItem: ExpenseSheet) => {
      // Callback cuando se crea un item - abrir diálogo de detalle
      loadExpenseSheets()
      
      if (updatedItem.id && expenseSheetDetailDialog.value) {
        // Ocultar el módulo principal antes de abrir el detalle
        showDialog.value = false
        
        // Abrir el diálogo de detalle
        expenseSheetDetailDialog.value.open(updatedItem.id)
      }
    }, () => {
      // Callback cuando se elimina un item
      loadExpenseSheets()
    })
  }
}

function onItemUpdated(_item: ExpenseSheet) {
  // Recargar la lista después de crear/editar
  loadExpenseSheets()
}

function onItemDeleted() {
  // Recargar la lista después de eliminar
  loadExpenseSheets()
}

function showDetail(item: ExpenseSheet) {
  if (expenseSheetDetailDialog.value && item.id) {
    // Ocultar el módulo principal antes de abrir el detalle
    showDialog.value = false
    
    // Abrir el diálogo de detalle
    expenseSheetDetailDialog.value.open(item.id)
  }
}

function onDetailDialogClosed() {
  // Volver a mostrar el módulo principal cuando se cierra el diálogo de detalle
  showDialog.value = true
  
  // Recargar la lista para reflejar posibles cambios
  loadExpenseSheets()
}

function onSendSuccess() {
  // Mostrar el toast cuando la hoja de gastos se envía correctamente
  // Usar setTimeout para que se vea después de que se cierre el diálogo
  setTimeout(() => {
    toast.add({
      severity: "success",
      summary: t("wkt.client.expensesheets.edit-dialog.send_ok"),
      life: 3000,
    })
  }, 500)
}

function loadExpenseSheets() {
  if (!company.value) {
    return
  }

  isLoading.value = true

  expenseSheetsApi.getExpenseSheets(
    { 
      companyId: company.value.id, 
      sortBy: 'id', 
      sortDesc: true 
    },
    (data) => {
      isLoading.value = false
      items.value = data

      // Lógica de licencia: determinar si mostrar botón de agregar
      if (company.value.licenseType === 0) {
        showAddButton.value = false
      } else if (company.value.licenseType === 1) {
        // BUSINESS: solo una hoja de gastos activa permitida
        showAddButton.value = items.value.filter(i => {
          const status = Number(i.status)
          return [-1, 0, 1, 2, 3].includes(status)
        }).length === 0
      } else if (company.value.licenseType === 2) {
        // ENTERPRISE: sin límites
        showAddButton.value = true
      }
    },
    (err: any) => {
      isLoading.value = false
      showAlert({ 
        text: err.message || 'Error al cargar hojas de gastos', 
        color: 'error'
      })
    }
  )
}

function checkMobile() {
  isMobile.value = window.innerWidth < 992
}

function updateCompanyAndRefresh() {
  const sessionInfo = gwtApi.getSessionInformation()
  
  if (!sessionInfo) {
    return
  }

  // Actualizar la compañía y recargar datos
  company.value = sessionInfo.company
  loadExpenseSheets()
}

function onReveal() {  
  // Patrón de Vue 2: retry recursivo hasta obtener la sesión
  const sessionInfo = gwtApi.getSessionInformation()
  
  if (!sessionInfo) {
    // Si no hay sesión, reintentar después de 1 segundo
    setTimeout(onReveal, 1000)
    return
  }

  // Sesión disponible, proceder con la carga
  company.value = sessionInfo.company
  loadExpenseSheets()
}

function refreshModule() {
  updateCompanyAndRefresh()
}

function open() {
  showDialog.value = true
  onReveal()
}

// Watch para detectar cambios en la información de sesión
watch(() => {
  const sessionInfo = gwtApi.getSessionInformation()
  return sessionInfo?.company?.id
}, (newCompanyId, oldCompanyId) => {
  // Si cambia el ID de la compañía, recargar
  if (newCompanyId && oldCompanyId && newCompanyId !== oldCompanyId) {
    updateCompanyAndRefresh()
  }
})

// Lifecycle
onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

// Exponer métodos para uso externo desde GWT
defineExpose({
  open,
  onReveal,
  refreshModule
})
</script>

<style scoped>
.expense-sheets-module {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  height: 100vh;
  width: 100%;
  background: var(--white);
  z-index: 100;
  padding-top: 10px;
  padding-bottom: 20px;
}

/* Header móvil (similar al header azul de GWT) */
.module-header-mobile {
  background-color: #185EA9;
  padding: 1rem 1.5rem;
  padding-top: max(1rem, env(safe-area-inset-top));
  margin: -10px -0.5rem 0 -0.5rem;
}

.module-title {
  color: white;
  font-size: 1.5em;
  font-weight: 500;
  margin: 0;
  padding: 0;
}

/* Título para tablet/desktop */
.module-title-desktop {
  font-size: 1.5em;
  font-weight: 500;
  color: var(--text-color);
  margin: 0 0 1rem 0;
  padding: 0.5rem 0;
}

.expense-sheets-container {
  padding: 0.5rem;
}

.expense-sheets-content {
  max-width: 100%;
  margin: 0 auto;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

/* Responsive */
@media (max-width: 768px) {
  .expense-sheets-module {
    height: calc(100vh - 200px);
    overflow: hidden;
    padding: 0;
  }
}

/* Ocultar en impresión */
@media print {
  .expense-sheets-module {
    display: none !important;
  }
}
</style>